
import React from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { Shirt } from 'lucide-react';

interface PaymentDetailsProps {
  fixedContribution: number;
  additionalContribution: number;
  totalAmount: number;
  tShirtSize?: string;
}

const PaymentDetails: React.FC<PaymentDetailsProps> = ({ 
  fixedContribution,
  additionalContribution,
  totalAmount,
  tShirtSize
}) => {
  // Format number to Vietnamese currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND',
      minimumFractionDigits: 0
    }).format(amount);
  };

  // Format T-shirt size for display - Updated for Nam_/Nu_ prefixes with new format
  const formatTShirtSize = (size?: string): string => {
    if (!size) return "L";
    
    if (size.startsWith('Nam_')) {
      return `${size.substring(4)} (Nam)`;
    } 
    
    if (size.startsWith('Nu_')) {
      return `${size.substring(3)} (Nữ)`;
    }
    
    return size;
  };

  return (
    <Card className="w-full border border-blue-200 shadow-md overflow-hidden">
      <CardContent className="p-0">
        <div className="bg-gradient-to-r from-blue-600 to-indigo-500 py-3 px-4">
          <h3 className="font-semibold text-lg text-center text-white">Chi tiết đóng góp</h3>
        </div>
        
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
          className="space-y-2 p-4"
        >
          <div className="flex justify-between py-3 border-b border-blue-100">
            <span className="text-blue-700">Đóng góp cơ bản:</span>
            <span className="font-medium text-blue-800">{formatCurrency(fixedContribution)}</span>
          </div>
          
          <div className="flex justify-between py-3 border-b border-blue-100">
            <span className="text-blue-700">Đóng góp thêm:</span>
            <span className="font-medium text-blue-800">{formatCurrency(additionalContribution)}</span>
          </div>

          {tShirtSize && (
            <div className="flex justify-between py-3 border-b border-blue-100 bg-blue-50 px-3 rounded-md">
              <span className="flex items-center text-blue-700">
                <Shirt className="h-5 w-5 mr-2 text-blue-600" />
                <span>Kích thước áo:</span>
              </span>
              <span className="font-bold text-indigo-600 bg-white px-3 py-1 rounded-full border border-indigo-200 shadow-sm">
                {formatTShirtSize(tShirtSize)}
              </span>
            </div>
          )}
          
          <div className="flex justify-between py-4 mt-2 border-t border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50 px-3 rounded-md">
            <span className="text-blue-800 font-semibold">Tổng cộng:</span>
            <span className="text-indigo-700 font-bold bg-white px-3 py-1 rounded-full border border-indigo-200 shadow-sm">
              {formatCurrency(totalAmount)}
            </span>
          </div>
        </motion.div>
      </CardContent>
    </Card>
  );
};

export default PaymentDetails;
